import { useState } from "react";
import { useFormik } from "formik";
import { Icons } from "assets";
import { toastMessage } from "shared/components/common/toast";
import { handleErrors } from "shared/utils/helper";
import { ChangePasswordService } from "shared/services/user";
import { passwordChangeValidation } from "shared/utils/validation";
import classNames from "classnames";
import styles from "./style.module.scss";
import CustomButton from "shared/components/common/customButton";
import CustomInput from "../customInput";

interface ChangePasswordSectionProps {
  title?: string;
}

const ChangePasswordSection = ({
  title = "Change Password",
}: ChangePasswordSectionProps) => {
  const [loading, setLoading] = useState(false);

  const formik = useFormik({
    initialValues: {
      currentPassword: "",
      newPassword: "",
      confirmNewPassword: "",
    },
    validationSchema: passwordChangeValidation,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        setLoading(true);
        setSubmitting(true);
        const response = await ChangePasswordService({
          currentPassword: values.currentPassword,
          password: values.newPassword,
          confirmPassword: values.confirmNewPassword,
        });

        if (response.status === 200) {
          const message =
            response.data?.message ||
            response.data?.data?.message ||
            "Password changed successfully";
          toastMessage("success", message);
          formik.resetForm();
        }
      } catch (error: any) {
        handleErrors(error);
      } finally {
        setLoading(false);
        setSubmitting(false);
      }
    },
  });

  return (
    <div className={classNames(styles.changePasswordEnclosure, "grid")}>
      <h2 className={classNames(styles.title)}>{title}</h2>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          formik.handleSubmit(e);
        }}
      >
        <div className={classNames(styles.passwordContents, "flex")}>
          <CustomInput
            label="Current Password"
            placeholder="•••••••••••"
            Icon={Icons.LockIcon}
            customIconStyle={classNames(styles.iconStyling)}
            isPassword={true}
            name="currentPassword"
            value={formik.values.currentPassword}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={
              formik.touched.currentPassword && formik.errors.currentPassword
                ? formik.errors.currentPassword
                : ""
            }
            required
            customInputContainer={styles.inputContainer}
          />

          <CustomInput
            label="New Password"
            Icon={Icons.LockIcon}
            placeholder="•••••••••••"
            isPassword={true}
            customIconStyle={classNames(styles.iconStyling)}
            name="newPassword"
            onBlur={formik.handleBlur}
            onChange={formik.handleChange}
            value={formik.values.newPassword}
            error={
              formik.touched.newPassword && formik.errors.newPassword
                ? formik.errors.newPassword
                : ""
            }
            required
            customInputContainer={styles.inputContainer}
          />

          <CustomInput
            label="Confirm Password"
            placeholder="•••••••••••"
            Icon={Icons.LockIcon}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            customIconStyle={classNames(styles.iconStyling)}
            name="confirmNewPassword"
            isPassword={true}
            value={formik.values.confirmNewPassword}
            error={
              formik.touched.confirmNewPassword &&
              formik.errors.confirmNewPassword
                ? formik.errors.confirmNewPassword
                : ""
            }
            required
            customInputContainer={styles.inputContainer}
          />
        </div>
        <div className={classNames(styles.buttonEnclosure, "mt-3")}>
          <CustomButton
            title="Save Changes"
            containerStyle={classNames(styles.submitButton)}
            type="submit"
            loading={loading}
            disabled={loading || formik.isSubmitting}
          />
        </div>
      </form>
    </div>
  );
};

export default ChangePasswordSection;
